/**
 * Task 27 (console integration): the one place that brings the console up
 * from an empty store to a live one. Before this, `ConsolePage` mounted real
 * panes over an empty store forever — nothing fetched the initial queue,
 * presence or identity, and nothing subscribed to the live feed, so every
 * surface sat on its own honest-empty state permanently, not because there
 * was nothing to show but because nothing had ever asked.
 *
 * Runs exactly once for the app's lifetime (called from `App.tsx`, which
 * never remounts as the router navigates between pages) — see App.tsx's own
 * comment for why this must not live in `ConsolePage`, which does mount and
 * unmount as the operator navigates to /settings, /history or a transcript
 * and back.
 *
 * ## Ordering: the stream-vs-initial-fetch race
 *
 * `subscribeStream` (src/lib/stream.ts) applies every SSE event straight onto
 * the store the moment it arrives — `thread` events call `upsertThread`,
 * which is a merge, not a replace (task-14-report.md's held-arrival work
 * spent two fix rounds making that merge correct; nothing here re-derives or
 * duplicates that logic). The one honest hazard is sequencing against the
 * *initial* `GET /ops/threads` snapshot this module also has to fetch:
 *
 *   1. Subscribe to the stream FIRST, before the initial fetch is even
 *      issued. Any `thread` event that lands during the fetch's flight goes
 *      straight into the (still-empty) store via the stream's own
 *      `upsertThread` call — nothing here needs to buffer it.
 *   2. When the initial `ops.threads('queue')` fetch resolves, apply it with
 *      `upsertThread` **per item**, never `setThreads` (which fully replaces
 *      the array). A wholesale replace at this point could silently drop a
 *      thread the stream already placed during the gap, if that thread
 *      happens not to be in this particular snapshot (a genuinely possible
 *      timing outcome, not a hypothetical) — that would be exactly the kind
 *      of loss the brief calls out. Per-item `upsertThread` instead merges:
 *      an id already present (from the stream) gets updated in place, a
 *      brand-new id from the snapshot gets appended, and anything the stream
 *      placed that the snapshot doesn't mention (yet) is left alone rather
 *      than deleted — nothing lost, and nothing duplicated, because
 *      `upsertThread`'s own id-based merge (task 12, held-aware since
 *      task 14) is what every other path in this app already trusts for
 *      exactly this.
 *
 * Presence needs none of this care: every `presence` read and every
 * `presence` SSE event already carries the *complete* peer roster (never a
 * delta), so `setPeers` replacing wholesale is correct regardless of which
 * one lands last — there is no analogous "loss" failure mode for peers.
 *
 * ## Identity
 *
 * `identity.whoAmI()` (src/lib/identity.ts) is the same shape of real,
 * always-resolving seam read as `aiOps.read()`/`modals/reads.ts`. Task 31
 * wired the route it calls (`GET /ops/whoami`) for real, so this now
 * resolves either a genuine signed-in identity or an honest `null` — never a
 * fabricated one. When it resolves a real identity, this is also the one
 * place that seeds `store.role`/`admin`/`superAdmin`/`covering` from the real
 * person's own stored values.
 *
 * NOT "flippable by the team" ANY MORE. This comment used to say the
 * opposite — role/admin were self-service toggles in the header, seeded here
 * only as a truthful *starting* position. Users-and-permissions design §1/§6
 * closed that: `RoleSegment`/`AdminCheckbox` are deleted, `role` and `admin`
 * are admin-assigned through the real Users pane (`src/components/settings/
 * UsersSection.tsx`, `PATCH /ops/users/:id`) now, and this seed is the ONLY
 * place any of these four fields is ever written client-side FROM A REAL
 * IDENTITY — a demotion or revoked admin still takes effect on the next
 * sign-in/whoami read, never by the console inventing its own value in
 * between. The one other writer is `InitialsMenu.tsx`'s `logOut`, which resets
 * three of the four (`role: 'dispatch', admin: false, superAdmin: false` —
 * not `covering`, which has no meaningful "signed out" value of its own and
 * is simply left stale until the next real read) to safe, signed-out
 * defaults alongside clearing `me`. That is not a second source of truth for
 * what these fields ARE, only the store forgetting what they WERE the moment
 * nobody is signed in to have them; the next bootstrap after a fresh sign-in
 * overwrites it here exactly the same way it always does.
 *
 * Either outcome also resolves `store.authStatus` out of its initial
 * `'checking'` state — `'signed-in'` on a real identity, `'signed-out'`
 * otherwise (including on the defensive `.catch()` below, which
 * `identity.whoAmI()` itself is documented to never actually reach).
 * `src/components/auth/AuthGate.tsx` is what actually acts on that value —
 * this module only ever sets it, never reasons about routing.
 *
 * ## Config (task-42, bug 1)
 *
 * `GET /ops/config` requires an authenticated staff actor, the same floor as
 * every other `/ops/*` read — so `ops.config()` is only ever attempted
 * inside the signed-in branch above, after `setAuthStatus('signed-in')`,
 * never speculatively at the top of this function alongside `threads`/
 * `presence`. Fetching it unconditionally there would mean firing it while
 * signed out, on every fresh load, for a guaranteed 401 the console would
 * then swallow the exact same silent way it already does for `threads`/
 * `presence` — the ordering discipline is the same shape as the stream-vs-
 * initial-fetch race documented above, just gated on "is anyone signed in"
 * instead of "did the snapshot arrive before the stream." Nothing else in
 * the console fetches config at boot (previously only the Settings page
 * did, and only once visited), which is exactly why `store.cfg` used to
 * stay `{}` for a whole fresh session and every reader of it — the Halo
 * ticket deep link, the header's product-name wordmark — silently took its
 * fallback path instead of the real configured value.
 */
import { ops } from './lib/ops'
import { identity } from './lib/identity'
import { subscribeStream } from './lib/stream'
import type { StreamDeps, StreamHandle } from './lib/stream'
import { useStore } from './store'
import type { ConsoleState } from './store'
import { setBadge, unreadCount } from './lib/badge'
import { refreshSession } from './lib/sessionRefresh'
import { loadRuntimeConfig } from './runtime-config'
import { selectPref } from './store'
import { defaultCtxOpenForWidth } from './lib/viewport'
import { notify } from './lib/notifications'
import { activity } from './lib/activity'

export interface BootstrapDeps {
  /** Test seam — forwarded to subscribeStream verbatim (fake EventSource, fixed URL). */
  streamDeps?: StreamDeps
}

/**
 * Fetches the initial queue/presence/identity and opens the live stream.
 * Returns the stream's handle so the caller can close it on unmount (in
 * practice, `App.tsx` for the lifetime of the whole app).
 */
/**
 * Refetch what the live feed cannot replay.
 *
 * `/ops/stream` carries no snapshot on connect — only mutations and the
 * periodic presence tick — so a reconnected feed is still showing whatever it
 * had before the drop until something happens to be published. This pulls the
 * queue and presence back to the truth immediately.
 *
 * `upsertThread` per item, never `setThreads`: a full replace would drop a
 * thread the rail is holding for another reason (see this module's header).
 */
function resyncAfterReconnect(): void {
  // THE VIEWER'S OWN SCOPE, not a hardcoded one.
  //
  // This refetched `'queue'` and upserted every row into the store no matter
  // what the person was looking at — so any stream reconnect quietly filled a
  // rail set to "mine" with the whole queue, while the filter chip still said
  // "mine". It looked like the coverage toggle resetting the view, because
  // that is a moment a reconnect happens to be likely.
  const { filter, techFilter } = useStore.getState()
  ops
    .threads(filter, techFilter ?? undefined)
    .then((threads) => {
      const { upsertThread } = useStore.getState()
      for (const thread of threads) upsertThread(thread)
    })
    .catch(() => {
      // Still offline. The next reconnect will resync again; nothing here
      // invents a queue it did not fetch.
    })

  ops
    .presence()
    .then((result) => {
      useStore.getState().setPeers(result.peers)
    })
    .catch(() => {
      // Same honest-empty reasoning as everywhere else in this module.
    })
}

/**
 * How long to wait before asking again when the server could not answer.
 *
 * Short, because the person is staring at a blank gate — and a container
 * restart is over in seconds.
 */
const WHOAMI_RETRY_MS = 2000

/**
 * Keep the app icon's badge in step with the rail.
 *
 * Subscribed here rather than in a component: the badge belongs to the
 * application, not to whatever happens to be mounted, and a console sitting on
 * the Settings page should still show what is waiting.
 *
 * Only writes when the number actually changes — the presence tick republishes
 * the whole rail every few seconds, and calling into the platform each time for
 * an unchanged value is pure waste.
 */
function trackBadge(): void {
  let last = -1
  const apply = (state: ConsoleState): void => {
    const count = unreadCount(state.threads)
    if (count === last) return
    last = count
    setBadge(count)
  }
  apply(useStore.getState())
  useStore.subscribe(apply)
}

/**
 * How often to re-assert "I am here" while nothing changes.
 *
 * Paired with the server's activity TTL, which is deliberately longer: two
 * missed heartbeats fall to away rather than one, so a single dropped request
 * on a Starlink connection does not blink somebody out of the team roster.
 */
const ACTIVITY_HEARTBEAT_MS = 30_000

/**
 * Tell the team whether this person is actually at the machine.
 *
 * BOOTSTRAP, NOT A COMPONENT, and that is the whole point. The thread beacon
 * only exists while a conversation is open, so it could never answer this:
 * somebody reading the rail, or sitting on Settings, is still at their desk.
 * This reports regardless of what is on screen.
 *
 * Changes are sent the moment they happen — locking a laptop hides the page,
 * which the tracker sees at once — so going away shows up in a second or two
 * rather than waiting out the TTL. The heartbeat only covers the case where
 * nothing changes for a long time.
 */
function trackActivity(): void {
  const report = (): void => {
    void ops.presenceActivity(activity.isActive()).catch(() => {
      // Best effort, exactly like the thread beacon: a missed report costs a
      // badge for one interval, never a conversation.
    })
  }
  report()
  activity.subscribe(report)
  const heartbeat = setInterval(report, ACTIVITY_HEARTBEAT_MS)
  // Never keeps a test run (or a short-lived process) alive.
  if (typeof heartbeat === 'object' && 'unref' in heartbeat) heartbeat.unref()
}

export function bootstrapConsole(deps: BootstrapDeps = {}): StreamHandle {
  trackBadge()
  trackActivity()
  const handle = subscribeStream(useStore, {
    onResync: resyncAfterReconnect,
    refreshSession: () =>
      refreshSession({
        resolveBase: async () => (await loadRuntimeConfig()).apiBase,
        onSignedOut: () => useStore.getState().setAuthStatus('signed-out'),
      }),
    onSignedOut: () => useStore.getState().setAuthStatus('signed-out'),
    onNewThread: (thread) => {
      const { prefs, me } = useStore.getState()
      notify({
        title: `New ${thread.channel} from ${thread.who}`,
        body: thread.client ?? 'Unknown company',
        meId: me?.id ?? null,
        // ownerId, not owner: `owner` is the display NAME. Comparing a name
        // against a staff id would never match, so every thread would have
        // looked unowned and notified everyone about everything.
        ownerId: thread.ownerId ?? null,
        sound: selectPref(prefs, 'p.sound', true),
        desktop: selectPref(prefs, 'p.desktop', true),
      })
    },
    ...deps.streamDeps,
  })

  ops
    .threads('queue')
    .then((threads) => {
      const { upsertThread } = useStore.getState()
      for (const thread of threads) upsertThread(thread)
    })
    .catch(() => {
      // Offline or the ops service isn't reachable yet: the rail's own
      // empty state ("Nothing needs you") is what shows — nothing here
      // fabricates a queue that was never actually fetched.
    })

  ops
    .presence()
    .then((result) => {
      useStore.getState().setPeers(result.peers)
    })
    .catch(() => {
      // Same honest-empty reasoning as above — an empty peer list is what
      // the rail's Techs menu and the coverage badges already handle.
    })

  identity
    .whoAmI()
    .then((me) => {
      const { setMe, set, setAuthStatus } = useStore.getState()
      if (me === 'unreachable') {
        // The server could not answer. Saying "signed out" here is what showed
        // the sign-in page to people whose session was fine, every time a
        // container restarted. Retry shortly; the auth gate keeps showing its
        // checking state, which is the truth.
        setTimeout(() => bootstrapConsole(deps), WHOAMI_RETRY_MS)
        return
      }
      if (!me) {
        setAuthStatus('signed-out')
        return
      }
      setMe(me)
      // A truthful starting position for the flippable role/admin toggles —
      // see this module's own header comment. Only ever applied once, here,
      // at bootstrap; nothing re-applies it later and stomps a manual flip.
      // `covering` joins them as of task 38: `GET /ops/whoami` carries the
      // caller's real, persisted flag (services/ops/src/routes/coverage.ts
      // is what now keeps it truthful), so a returning dispatcher who left
      // their coverage on sees "Covering" immediately rather than the
      // store's honest-but-wrong `false` default until they touch the
      // switch themselves. `?? false` only matters against an older cached
      // client build talking to a whoami response that predates this field.
      set({ role: me.role, admin: me.admin, covering: me.covering ?? false, superAdmin: me.superAdmin ?? false })
      setAuthStatus('signed-in')

      // Bug 1 (task-42): `GET /ops/config` requires an authenticated staff
      // actor (services/ops/src/server.ts), so this can only fire here —
      // right after a real identity has actually resolved — never before,
      // and never at all when identity resolved to `null` above. Before
      // this, nothing in the console ever called `ops.config()` at all
      // (only the Settings page did, and only once visited), so `store.cfg`
      // stayed `{}` for the whole session on a fresh load: the Halo ticket
      // deep link built a relative `/ticket?id=…` URL against the console's
      // own origin instead of Halo's host, and the header silently fell
      // back to its generic wordmark instead of the configured product
      // name. Fire-and-forget like `threads`/`presence` above — a dropped
      // config read is exactly the same "offline right now" shape those
      // already handle, and every reader of `cfg` (haloTicketUrl chief
      // among them) already has to treat a missing field as an honest
      // absence, never a guess.
      // Per-user preferences. Same authenticated-staff requirement as config,
      // so it fires here rather than at page load.
      ops
        .preferences()
        .then((result) => {
          const { setPrefs, set } = useStore.getState()
          setPrefs(result.values)
          // p.ctx — the context pane's default state, applied once, at load.
          // Not reapplied later: it is a starting position, and stomping a
          // pane the person deliberately collapsed mid-shift would be worse
          // than not honouring it at all.
          //
          // When the person has NO saved p.ctx yet, the fallback is
          // width-based: open on a roomy desktop, collapsed on a narrow
          // three-pane width (a phone in landscape, a small tablet) where the
          // context pane crowds the thread. An explicit p.ctx always wins.
          set({ ctxOpen: selectPref(result.values, 'p.ctx', defaultCtxOpenForWidth()) })
        })
        .catch(() => {
          // Same honest-empty reasoning as everything else here: the schema
          // defaults apply, which is exactly what a person who has never
          // touched a toggle should get.
        })

      ops
        .config()
        .then((result) => {
          useStore.getState().setCfg(result.values)
          if (result.env) useStore.getState().setCfgEnv(result.env)
        })
        .catch(() => {
          // Same honest-empty reasoning as threads/presence — cfg simply
          // stays `{}` (or whatever it already was), and every reader of it
          // already renders that as "not configured yet", not an error.
        })

      // Task 7 (multiple SMS lines): `GET /ops/lines` is staff-readable, not
      // admin-gated (LinesSection.tsx's own header comment), but it is still
      // an `/ops/*` read, so it fires here alongside config/preferences —
      // after a real identity has resolved, never before. A failed fetch
      // leaves `store.lines` at its honest `null` default rather than `[]`:
      // the header/rail gate on `(lines?.length ?? 0) > 1`, so `null` and a
      // genuinely-empty `[]` must not be conflated with "definitely one or
      // zero lines" — both simply render no line label, degraded, never
      // broken. Nothing here blocks on this call; threads/presence/identity
      // already resolved above regardless of how this settles.
      ops
        .lines()
        .then((lines) => {
          useStore.getState().setLines(lines)
        })
        .catch(() => {
          // Offline or the ops service isn't reachable: `lines` stays
          // `null`, and every reader of it already treats that as "don't
          // know" rather than "zero lines configured".
        })
    })
    .catch(() => {
      // whoAmI() itself never rejects (see src/lib/identity.ts) — this catch
      // exists only so a defensive future change to that contract can never
      // crash the whole app's bootstrap. The safe direction on any unexpected
      // failure here is the same as an honest `null`: signed out, not stuck
      // in 'checking' forever (which would leave the auth gate showing its
      // placeholder indefinitely instead of ever reaching sign-in).
      useStore.getState().setAuthStatus('signed-out')
    })

  return handle
}
